// Pattern: docs/design-patterns/frontend/state-management — client state only (no server data)

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { PatternDomain, PatternSeverity } from '~/types';

interface PatternsState {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedDomains: Set<PatternDomain>;
  toggleDomain: (domain: PatternDomain) => void;
  selectedSeverities: Set<PatternSeverity>;
  toggleSeverity: (severity: PatternSeverity) => void;
  selectedRefArchId: string | null;
  setSelectedRefArchId: (id: string | null) => void;
  expandedDomains: Set<string>;
  toggleDomainExpansion: (domain: string) => void;
  resetFilters: () => void;

  // Sidebar expansion state (persisted to localStorage)
  refArchExpanded: boolean;
  setRefArchExpanded: (expanded: boolean) => void;
  toggleRefArchExpanded: () => void;
  expandedTiers: Set<string>;
  toggleTier: (tier: string) => void;
  expandedTypes: Set<string>;
  toggleType: (type: string) => void;
  patternsExpanded: boolean;
  setPatternsExpanded: (expanded: boolean) => void;
  togglePatternsExpanded: () => void;
  sidebarExpandedDomains: Set<string>;
  toggleSidebarDomain: (domain: string) => void;

  frameworksExpanded: boolean;
  setFrameworksExpanded: (expanded: boolean) => void;
  toggleFrameworksExpanded: () => void;
  expandedTestLayers: Set<string>;
  toggleTestLayer: (layer: string) => void;

  // Last visited path within /patterns (persisted for cross-section navigation)
  lastPatternsPath: string;
  setLastPatternsPath: (path: string) => void;
}

/** Convert Set to array for JSON serialization */
function setToArray<T>(s: Set<T>): T[] {
  return [...s];
}

export const usePatternsStore = create<PatternsState>()(
  persist(
    set => ({
      searchQuery: '',
      setSearchQuery: (query: string): void => {
        set({ searchQuery: query });
      },

      selectedDomains: new Set<PatternDomain>(),
      toggleDomain: (domain: PatternDomain): void => {
        set(state => {
          const newSet = new Set(state.selectedDomains);
          if (newSet.has(domain)) newSet.delete(domain);
          else newSet.add(domain);
          return { selectedDomains: newSet };
        });
      },

      selectedSeverities: new Set<PatternSeverity>(),
      toggleSeverity: (severity: PatternSeverity): void => {
        set(state => {
          const newSet = new Set(state.selectedSeverities);
          if (newSet.has(severity)) newSet.delete(severity);
          else newSet.add(severity);
          return { selectedSeverities: newSet };
        });
      },

      selectedRefArchId: null,
      setSelectedRefArchId: (id: string | null): void => {
        set({ selectedRefArchId: id });
      },

      expandedDomains: new Set<string>(),
      toggleDomainExpansion: (domain: string): void => {
        set(state => {
          const newSet = new Set(state.expandedDomains);
          if (newSet.has(domain)) newSet.delete(domain);
          else newSet.add(domain);
          return { expandedDomains: newSet };
        });
      },

      resetFilters: (): void => {
        set({
          searchQuery: '',
          selectedDomains: new Set<PatternDomain>(),
          selectedSeverities: new Set<PatternSeverity>(),
          selectedRefArchId: null,
        });
      },

      // Sidebar expansion state
      refArchExpanded: true,
      setRefArchExpanded: (expanded: boolean): void => {
        set({ refArchExpanded: expanded });
      },
      toggleRefArchExpanded: (): void => {
        set(state => ({ refArchExpanded: !state.refArchExpanded }));
      },

      expandedTiers: new Set<string>(),
      toggleTier: (tier: string): void => {
        set(state => {
          const next = new Set(state.expandedTiers);
          if (next.has(tier)) next.delete(tier);
          else next.add(tier);
          return { expandedTiers: next };
        });
      },

      expandedTypes: new Set<string>(),
      toggleType: (type: string): void => {
        set(state => {
          const next = new Set(state.expandedTypes);
          if (next.has(type)) next.delete(type);
          else next.add(type);
          return { expandedTypes: next };
        });
      },

      patternsExpanded: true,
      setPatternsExpanded: (expanded: boolean): void => {
        set({ patternsExpanded: expanded });
      },
      togglePatternsExpanded: (): void => {
        set(state => ({ patternsExpanded: !state.patternsExpanded }));
      },

      sidebarExpandedDomains: new Set<string>(),
      toggleSidebarDomain: (domain: string): void => {
        set(state => {
          const next = new Set(state.sidebarExpandedDomains);
          if (next.has(domain)) next.delete(domain);
          else next.add(domain);
          return { sidebarExpandedDomains: next };
        });
      },

      frameworksExpanded: true,
      setFrameworksExpanded: (expanded: boolean): void => {
        set({ frameworksExpanded: expanded });
      },
      toggleFrameworksExpanded: (): void => {
        set(state => ({ frameworksExpanded: !state.frameworksExpanded }));
      },

      expandedTestLayers: new Set<string>(),
      toggleTestLayer: (layer: string): void => {
        set(state => {
          const next = new Set(state.expandedTestLayers);
          if (next.has(layer)) next.delete(layer);
          else next.add(layer);
          return { expandedTestLayers: next };
        });
      },

      lastPatternsPath: '/patterns/architectures',
      setLastPatternsPath: (path: string): void => {
        set({ lastPatternsPath: path });
      },
    }),
    {
      name: 'patterns-sidebar-state',
      // Only persist sidebar expansion state — filter/search state resets on reload
      partialize: state => ({
        refArchExpanded: state.refArchExpanded,
        expandedTiers: setToArray(state.expandedTiers),
        expandedTypes: setToArray(state.expandedTypes),
        patternsExpanded: state.patternsExpanded,
        sidebarExpandedDomains: setToArray(state.sidebarExpandedDomains),
        frameworksExpanded: state.frameworksExpanded,
        expandedTestLayers: setToArray(state.expandedTestLayers),
        lastPatternsPath: state.lastPatternsPath,
      }),
      // Rehydrate arrays back into Sets
      merge: (persisted, current) => {
        const p = persisted as Record<string, unknown> | undefined;
        if (!p) return current;
        return {
          ...current,
          refArchExpanded:
            typeof p['refArchExpanded'] === 'boolean'
              ? p['refArchExpanded']
              : current.refArchExpanded,
          expandedTiers: new Set(
            Array.isArray(p['expandedTiers']) ? (p['expandedTiers'] as string[]) : [],
          ),
          expandedTypes: new Set(
            Array.isArray(p['expandedTypes']) ? (p['expandedTypes'] as string[]) : [],
          ),
          patternsExpanded:
            typeof p['patternsExpanded'] === 'boolean'
              ? p['patternsExpanded']
              : current.patternsExpanded,
          sidebarExpandedDomains: new Set(
            Array.isArray(p['sidebarExpandedDomains'])
              ? (p['sidebarExpandedDomains'] as string[])
              : [],
          ),
          frameworksExpanded:
            typeof p['frameworksExpanded'] === 'boolean'
              ? p['frameworksExpanded']
              : current.frameworksExpanded,
          expandedTestLayers: new Set(
            Array.isArray(p['expandedTestLayers']) ? (p['expandedTestLayers'] as string[]) : [],
          ),
          lastPatternsPath:
            typeof p['lastPatternsPath'] === 'string'
              ? p['lastPatternsPath']
              : current.lastPatternsPath,
        };
      },
    },
  ),
);
